import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateOtp, validateOtp } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignInModal, setShowSignInModal] = useState(false);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Listen for profile updates from EditProfileModal and update state immediately
  useEffect(() => {
    const onUserProfileUpdated = (e) => {
      try {
        const updatedFullName = e?.detail?.fullName || '';
        setCurrentUser((prev) => {
          const name = updatedFullName || prev?.name || prev?.fullName || '';
          const initials = name
            ? name
                .split(' ')
                .filter(Boolean)
                .map((n) => n[0])
                .join('')
                .toUpperCase()
            : (prev?.initials || 'U');
          const next = { ...prev, name, fullName: name, initials };
          return next;
        });
      } catch (_) {
        // ignore
      }
    };
    window.addEventListener('userProfileUpdated', onUserProfileUpdated);
    return () => window.removeEventListener('userProfileUpdated', onUserProfileUpdated);
  }, []);

  const checkAuthStatus = () => {
    try {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('authToken');
      const userMobileNumber = localStorage.getItem('userMobileNumber');
      const candidateId = localStorage.getItem('candidateId');

      if (user && token) {
        const userData = JSON.parse(user);
        setCurrentUser(userData);
        setIsAuthenticated(true);
        
        // Ensure mobile number is set
        if (!userMobileNumber && userData.mobile) {
          localStorage.setItem('userMobileNumber', userData.mobile.replace(/\D/g, ''));
        }
        
        // Ensure candidate ID is set
        if (!candidateId && userData.candidateId) {
          localStorage.setItem('candidateId', userData.candidateId);
        }
      } else {
        setCurrentUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setCurrentUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (mobileNumber, otp) => {
    setLoading(true);
    setError('');
    
    try {
      const result = await validateOtp(mobileNumber, otp);
      console.log('[AuthContext] SignIn result:', result);
      
      if (result.success) {
        const userData = {
          name: result.data?.name || 'User',
          email: result.data?.email || '',
          mobile: `+91 ${mobileNumber}`,
          mobileNumber: mobileNumber,
          candidateId: result.data?.candidateId || null,
          initials: result.data?.name ? result.data.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'
        };

        console.log('[AuthContext] Storing candidate ID:', result.data?.candidateId);

        // Save to localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('authToken', result.data?.token || 'demo-token');
        localStorage.setItem('userMobileNumber', mobileNumber);
        localStorage.setItem('candidateId', result.data?.candidateId || '');
        localStorage.setItem('isAuthenticated', 'true');

        setCurrentUser(userData);
        setIsAuthenticated(true);
        
        // Navigate to home page after successful login
        setTimeout(() => {
          navigate('/');
        }, 1500);
        
        return { success: true, message: 'OTP validated successfully' };
      } else {
        return { success: false, error: result.error || 'Invalid OTP' };
      }
    } catch (error) {
      console.error('Sign in error:', error);
      setLoading(false);
      return { success: false, error: 'Failed to sign in. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (mobileNumber) => {
    try {
      const result = await generateOtp(mobileNumber);
      return result;
    } catch (error) {
      console.error('Send OTP error:', error);
      return { success: false, error: 'Failed to send OTP. Please try again.' };
    }
  };

  const signOut = () => {
    // Remove all authentication data from localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userMobileNumber');
    localStorage.removeItem('candidateId');
    localStorage.removeItem('isAuthenticated');
    
    // Remove user preferences
    localStorage.removeItem('userPreferredLanguage');
    
    // Reset authentication state
    setCurrentUser(null);
    setIsAuthenticated(false);
    
    console.log('User signed out successfully - all data and preferences cleared');
    
    // Navigate to home page
    navigate('/');
  };

  const showSignIn = () => { 
    setShowSignInModal(true);
  };

  const hideSignIn = () => {
    setShowSignInModal(false);
  };

  const value = {
    isAuthenticated,
    currentUser,
    loading,
    error,
    showSignInModal,
    showSignIn,
    hideSignIn,
    signIn,
    signOut,
    sendOtp,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
